/**
 * Utility functions for SRP (Search Results Page) SEO handling
 * Implements canonical URL generation, robots meta tags, and page parameter handling
 */

import { isRecognizedSrpFilterAttribute } from './build-active-filters';

const BASE_URL = 'https://www.jobcrawls.com';

/**
 * Extracts the page number from URL search parameters
 * Handles multiple formats: page=2, en_search[page]=2, fi_hae[page]=2, etc.
 * Returns the page number (1-indexed) or null if not found or invalid
 */
export function extractPageNumber(url: string): number | null {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://example.com${url}`);
    const searchParams = urlObj.searchParams;
    
    // The SRP's own `page` parameter is 0-based (utils/search-router/url-handling.ts: page 0 is
    // the first page and omitted, `?page=1` is the second), so the human page number is one
    // higher. Until 2026-09-04 this returned the raw value, which made every second page
    // index, follow with a canonical to page one and a "(Sivu 2)" suffix on the third page.
    const simplePage = searchParams.get('page');
    if (simplePage) {
      const pageIndex = parseInt(simplePage, 10);
      if (!isNaN(pageIndex) && pageIndex > 0) {
        return pageIndex + 1;
      }
    }
    
    // Check for Algolia format parameters: [page] anywhere in key
    for (const [key, value] of searchParams.entries()) {
      if (key.includes('[page]')) {
        const pageNum = parseInt(value, 10);
        if (!isNaN(pageNum) && pageNum > 0) {
          return pageNum;
        }
      }
    }
    
    return null;
  } catch {
    return null;
  }
}

/** The SRP `page` query value for a human page number (1-based in, 0-based out): page 2 is `?page=1`. */
export function pageParamForPageNumber(pageNum: number): string {
  return String(pageNum - 1);
}

/**
 * Checks if URL has any query parameters other than page
 * Returns true if there are other parameters, false otherwise
 */
export function hasNonPageQueryParams(url: string): boolean {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://example.com${url}`);
    const searchParams = urlObj.searchParams;
    
    // Check all parameters
    for (const [key] of searchParams.entries()) {
      // Skip page parameters
      if (key === 'page' || key.includes('[page]')) {
        continue;
      }
      // Found a non-page parameter
      return true;
    }
    
    return false;
  } catch {
    return false;
  }
}

/**
 * Lists every query key (besides `page`) that doesn't map to a recognized SRP filter attribute
 * (see `build-active-filters.ts`'s `isRecognizedSrpFilterAttribute`). Such params produce zero
 * `ActiveFilter`s, so `getRobotsDirective` would otherwise treat the URL as filterless and
 * return `index, follow` — this is the safety net that forces noindex instead for query shapes
 * the filter/robots policy has no opinion on. `hasUnrecognizedQueryParams` is a thin wrapper
 * over this for the common boolean check.
 */
export function getUnrecognizedQueryParamKeys(url: string): string[] {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://example.com${url}`);
    const unrecognized: string[] = [];
    for (const [key] of urlObj.searchParams.entries()) {
      if (key === 'page' || key.includes('[page]')) {
        continue;
      }
      if (!isRecognizedSrpFilterAttribute(key)) {
        unrecognized.push(key);
      }
    }
    return unrecognized;
  } catch {
    return [];
  }
}

export function hasUnrecognizedQueryParams(url: string): boolean {
  return getUnrecognizedQueryParamKeys(url).length > 0;
}

/**
 * Checks if URL has ONLY page parameter (and no other query params)
 */
export function hasOnlyPageParam(url: string): boolean {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://example.com${url}`);
    const searchParams = urlObj.searchParams;
    
    if (searchParams.toString().length === 0) {
      return false;
    }
    
    // Check if all parameters are page-related
    for (const [key] of searchParams.entries()) {
      if (key !== 'page' && !key.includes('[page]')) {
        return false;
      }
    }
    
    // Must have at least one page parameter
    return extractPageNumber(url) !== null;
  } catch {
    return false;
  }
}

/**
 * Generates canonical URL for SRP pages according to rules:
 * - Strip all query parameters, except 'page' (preserved so paginated pages self-canonicalize)
 * - Base jobs pages (/en/jobs, /fi/tyopaikat, /sv/lediga-jobb) self-canonicalize, including page 2+
 */
export function generateSRPCanonicalUrl(url: string, baseUrl: string = BASE_URL): string {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `${baseUrl}${url}`);
    const pathname = urlObj.pathname;

    // Build canonical URL with path only (strips all query params)...
    const canonicalUrl = new URL(pathname, baseUrl);
    // ...then restore the page param so paginated results don't collapse to page 1.
    const pageNum = extractPageNumber(url);
    if (pageNum !== null && pageNum > 1) {
      canonicalUrl.searchParams.set('page', pageParamForPageNumber(pageNum));
    }
    return canonicalUrl.toString();
  } catch {
    // Fallback: try to extract locale and return homepage
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `${baseUrl}${url}`);
      const localeMatch = urlObj.pathname.match(/^\/(en|fi|sv)/);
      if (localeMatch) {
        return `${baseUrl}/${localeMatch[1]}`;
      }
    } catch {}
    
    return `${baseUrl}/en`;
  }
}

/**
 * Determines robots meta tag content for SRP pages:
 * - Default: index,follow
 * - If query parameters present (except page): noindex,follow
 * - If only page parameter: index,follow
 */
export function determineSRPRobotsMeta(url: string): string {
  const hasOtherParams = hasNonPageQueryParams(url);
  
  if (hasOtherParams) {
    return 'noindex, follow';
  }
  
  return 'index, follow';
}

/**
 * Generates hreflang URLs from canonical URL
 * Preserves page parameter if present in canonical
 */
export function generateHreflangFromCanonical(
  canonicalUrl: string,
  currentLocale: 'en' | 'fi' | 'sv',
  baseUrl: string = BASE_URL
): Array<{ href: string; hrefLang: string }> {
  try {
    const canonical = new URL(canonicalUrl);
    const pageParam = canonical.searchParams.get('page');
    
    // Extract locale from canonical pathname
    const localeMatch = canonical.pathname.match(/^\/(en|fi|sv)/);
    const canonicalLocale = (localeMatch?.[1] || 'en') as 'en' | 'fi' | 'sv';
    
    // Map locales to their jobs path segments
    const jobsSegments: Record<'en' | 'fi' | 'sv', string> = {
      'en': 'jobs',
      'fi': 'tyopaikat',
      'sv': 'lediga-jobb'
    };
    
    // Generate hreflang URLs for other locales
    const alternates: Array<{ href: string; hrefLang: string }> = [];
    const locales: Array<'en' | 'fi' | 'sv'> = ['en', 'fi', 'sv'];
    
    for (const locale of locales) {
      if (locale === currentLocale) {
        continue;
      }
      
      // Build URL for this locale
      const hreflangUrl = new URL(`/${locale}/${jobsSegments[locale]}`, baseUrl);
      
      // Preserve page parameter if present
      if (pageParam) {
        hreflangUrl.searchParams.set('page', pageParam);
      }
      
      alternates.push({
        href: hreflangUrl.toString(),
        hrefLang: locale
      });
    }
    
    return alternates;
  } catch {
    return [];
  }
}

